import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: Placement;
  maxWidth?: number;
  className?: string;
  delay?: number;
}

const transforms: Record<Placement, string> = {
  top: 'translate(-50%, -100%) translateY(-8px)',
  bottom: 'translate(-50%, 0) translateY(8px)',
  left: 'translate(-100%, -50%) translateX(-8px)',
  right: 'translate(0, -50%) translateX(8px)',
};

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = 'top',
  maxWidth = 280,
  className = '',
  delay = 200,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || typeof window === 'undefined') return;

    const rect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    let top = rect.top + scrollY;
    let left = rect.left + scrollX;

    switch (placement) {
      case 'bottom':
        top = top + rect.height;
        left = left + rect.width / 2;
        break;
      case 'left':
        top = top + rect.height / 2;
        break;
      case 'right':
        top = top + rect.height / 2;
        left = left + rect.width;
        break;
      case 'top':
      default:
        left = left + rect.width / 2;
        break;
    }

    setCoords({ top, left });
  }, [placement]);

  useEffect(() => {
    if (!visible) return undefined;

    calculatePosition();
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [visible, calculatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleShow = () => {
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  };

  const handleHide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setVisible(false);
  };

  const isVertical = placement === 'top' || placement === 'bottom';

  const portalContent =
    visible && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={`pointer-events-none fixed z-[9999] text-sm ${
              isVertical ? 'flex flex-col items-center' : 'flex items-center'
            } ${className}`}
            style={{
              top: coords.top,
              left: coords.left,
              transform: transforms[placement] || transforms.top,
              maxWidth,
            }}
          >
            <div className="rounded-lg px-3 py-2 shadow-lg bg-gray-900 text-white border border-gray-700">
              {content}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex items-center"
        onMouseEnter={handleShow}
        onMouseLeave={handleHide}
        onFocus={handleShow}
        onBlur={handleHide}
      >
        {children}
      </span>
      {portalContent}
    </>
  );
};

export default Tooltip;
